/**
 * Corrections to docx-preview's layout where it differs from Word, checked line by line against Word's
 * own positions for the summons letter. Run on the rendered pages after docx-preview has set its tab
 * stops (it does so 500 ms after rendering) and before the pages are zoomed to fit.
 */

const isTab = (element: Element) => /-tab-stop$/.test(element.className);

/** The single-spaced line height of a font, measured outside the document so none of its rules apply. */
const naturalLineHeight = (fontFamily: string, fontSizePx: number): number => {
    const probe = document.createElement('div');
    probe.textContent = 'Xg';
    probe.style.cssText = 'position: absolute; visibility: hidden; display: inline-block; min-height: 0; padding: 0; margin: 0; line-height: normal;';
    probe.style.fontFamily = fontFamily;
    probe.style.fontSize = fontSizePx + 'px';
    document.body.appendChild(probe);
    const height = probe.getBoundingClientRect().height;
    document.body.removeChild(probe);
    return height;
}

const correctDocxLayout = (container: HTMLElement) => {
    const ptToPx = 96 / 72;
    const tabs = Array.from(container.querySelectorAll<HTMLElement>('section.docx span')).filter(isTab);

    // docx-preview draws a tab as a non-breaking space plus word-spacing equal to the gap, so each tab
    // ends one space-width past its stop. Note where each was meant to end before anything moves.
    const targets = tabs.map(tab => tab.getBoundingClientRect().left + (parseFloat(tab.style.wordSpacing) || 0) * ptToPx);

    let previousSize = 0;
    container.querySelectorAll<HTMLElement>('section.docx p').forEach(p => {
        // A line's height comes from its text, as in Word. docx-preview sizes tabs, and the paragraph's
        // own line box, from the paragraph style instead (legal's "Subtitle" style is 20pt; its lines
        // hold 11pt text). Tabs and text can share a span, so go by the text itself.
        let largest = 0;
        let textFont = '';
        const walker = document.createTreeWalker(p, NodeFilter.SHOW_TEXT);
        for (let node = walker.nextNode(); node; node = walker.nextNode()) {
            const parent = node.parentElement;
            if (parent && !isTab(parent) && (node.textContent ?? '').length > 0) {
                const style = getComputedStyle(parent);
                const size = parseFloat(style.fontSize);
                if (size > largest) {
                    largest = size;
                    textFont = style.fontFamily;
                }
            }
        }
        // a blank paragraph has no text to go by; it takes the size of the text above it
        if (largest === 0) {
            if ((p.textContent ?? '').length > 0 || previousSize === 0)
                return;
            largest = previousSize;
        }
        previousSize = largest;

        p.querySelectorAll<HTMLElement>('span').forEach(span => {
            if (isTab(span))
                span.style.fontSize = largest + 'px';
        });
        p.style.fontSize = largest + 'px';

        // Word's line spacing ("1.5 lines", "Multiple 1.08") multiplies the font's natural line height;
        // CSS's unitless line-height multiplies the font size, so those lines came out ~13% short
        const lineHeight = p.style.lineHeight.trim();
        if (/^[\d.]+$/.test(lineHeight)) {
            p.style.lineHeight = (parseFloat(lineHeight) * naturalLineHeight(textFont || getComputedStyle(p).fontFamily, largest)) + 'px';
        }
    });

    // Redo the tabs left to right so each ends exactly at its stop.
    tabs.forEach((tab, i) => {
        tab.style.wordSpacing = '0px';
        const box = tab.getBoundingClientRect();
        tab.style.wordSpacing = Math.max(0, targets[i] - box.left - box.width) + 'px';
    });

    alignFillLinesWithUnderscoreLines(container);
    drawTabUnderlinesAsLines(container);
}

/**
 * docx-preview draws a tab as a stretched space, and Chrome does not underline spaces at the end of a
 * line, so a fill-in line's underline (the summons' "[Name], [Title] ______") was not drawn at all.
 * Redraw each underlined tab as an empty box of the same width with its own bottom line, which is always
 * drawn. Run last, once every tab has its final width.
 */
const drawTabUnderlinesAsLines = (container: HTMLElement) => {
    Array.from(container.querySelectorAll<HTMLElement>('section.docx span')).filter(isTab).forEach(tab => {
        if (!getComputedStyle(tab).textDecorationLine.includes('underline'))
            return;
        const width = tab.getBoundingClientRect().width;
        tab.textContent = '';
        tab.style.wordSpacing = '';
        tab.style.textDecoration = 'none';
        tab.style.display = 'inline-block';
        tab.style.width = width + 'px';
        tab.style.borderBottom = '1px solid currentColor';
        // where a text underline sits, just below the baseline
        tab.style.verticalAlign = '-0.12em';
    });
}

/** How far apart a fill-in tab and an underscore line can end and still be meant to line up. */
const ALIGN_TOLERANCE_PX = 16;

/**
 * A line ending in an underlined tab (a fill-in line, like the summons' name-and-title line) is meant to
 * end where the underscore line above it ends; in Word both end at the same point. The browser draws
 * underscores slightly wider than Word (about 8px over the signature line's 50), so the underscore line
 * overshoots. Stretch the fill-in tab to meet it, as it does in Word.
 */
const alignFillLinesWithUnderscoreLines = (container: HTMLElement) => {
    const paragraphs = Array.from(container.querySelectorAll<HTMLElement>('section.docx p'));
    paragraphs.forEach((p, index) => {
        const tabs = Array.from(p.querySelectorAll<HTMLElement>('span')).filter(isTab);
        const fill = tabs[tabs.length - 1];
        if (!fill || getComputedStyle(fill).textDecorationLine !== 'underline')
            return;
        // nothing may follow the tab: it fills to the end of the line
        const after = document.createRange();
        after.setStartAfter(fill);
        after.setEndAfter(p.lastChild!);
        if (after.toString().trim().length > 0)
            return;

        // the nearest underscore line above, within a few lines
        const line = paragraphs.slice(Math.max(0, index - 4), index).reverse().find(q => /^_{10,}$/.test((q.textContent ?? '').trim()));
        if (!line)
            return;
        const lineRange = document.createRange();
        lineRange.selectNodeContents(line);
        const difference = lineRange.getBoundingClientRect().right - fill.getBoundingClientRect().right;
        if (Math.abs(difference) <= ALIGN_TOLERANCE_PX)
            fill.style.wordSpacing = Math.max(0, parseFloat(fill.style.wordSpacing) + difference) + 'px';
    });
}

export default correctDocxLayout;
